/**
 * useGuides Hooks
 *
 * Manages per-page user guides from Firestore.
 *
 * Architecture (mirrors announcements):
 * - useGuidesListener: Sets up Firestore listener, called ONCE in AuthenticatedLayout
 * - useGuides: Reads from Zustand store, used by any component
 *
 * This eliminates redundant Firestore subscriptions — one listener populates
 * the store, and all consumers read from it for free.
 */

import { useCallback, useMemo } from 'react';
import { useFirebaseStore } from '../store/firebaseStore';
import { useFirestoreCollection } from './firestore';
import type { DocumentData, Timestamp } from 'firebase/firestore';
import { useGuidesStore } from '../store/guidesStore';
import { getGuidesCollectionPath } from '../config/collections';
import type { PageGuide } from '../../guides-react/types';

/**
 * Parse guide document from Firestore
 */
function parseGuide(id: string, data: DocumentData): PageGuide {
  const lastEditedAt = data.lastEditedAt as Timestamp;
  const createdAt = data.createdAt as Timestamp;

  const guide: PageGuide = {
    id,
    contentJSON: data.contentJSON || '',
    contentHTML: data.contentHTML || '',
    published: data.published === true,
    lastEditedBy: data.lastEditedBy || '',
    lastEditedAt: lastEditedAt?.toDate() || new Date(),
    createdBy: data.createdBy || '',
    createdAt: createdAt?.toDate() || new Date(),
  };

  if (data.imageUrls && Array.isArray(data.imageUrls)) {
    guide.imageUrls = data.imageUrls;
  }
  if (data.summary) {
    guide.summary = data.summary;
  }
  if (data.tags && Array.isArray(data.tags)) {
    guide.tags = data.tags;
  }

  return guide;
}

/**
 * Hook to set up the Firestore guides listener.
 *
 * IMPORTANT: Call this ONCE in AuthenticatedLayout.
 * It populates the Zustand store that all components read from.
 *
 * @param enabled - Only activate listener when true (default: true). Pass isConnected
 *                  to prevent listener setup before Firebase auth completes.
 */
export const useGuidesListener = (enabled: boolean = true): void => {
  const db = useFirebaseStore((state) => state.db);
  const { setGuides, setIsLoading, setError } = useGuidesStore();

  const collectionPath = useMemo(() => getGuidesCollectionPath(), []);

  const handleGuidesLoaded = useCallback(
    (items: PageGuide[]) => {
      setGuides(items);
    },
    [setGuides]
  );

  const handleLoadingChange = useCallback(
    (loading: boolean) => {
      setIsLoading(loading);
    },
    [setIsLoading]
  );

  const handleError = useCallback(
    (errorMessage: string | null) => {
      setError(errorMessage);
    },
    [setError]
  );

  useFirestoreCollection<PageGuide>({
    db,
    collectionPath,
    parseDocument: parseGuide,
    onDataLoaded: handleGuidesLoaded,
    onLoadingChange: handleLoadingChange,
    onError: handleError,
    enabled: enabled && !!db,
  });
};

/** Return type for useGuides hook */
export interface UseGuidesReturn {
  /** Check if a published guide exists for a given pageId */
  hasGuide: (pageId: string) => boolean;
  /** Get the guide for a given pageId */
  getGuide: (pageId: string) => PageGuide | undefined;
  /** All published guides */
  publishedGuides: PageGuide[];
  /** All guides (published and draft) */
  allGuides: PageGuide[];
  /** Loading state */
  isLoading: boolean;
}

/**
 * Hook for consuming guides data.
 *
 * Reads from the Zustand store populated by useGuidesListener.
 * Safe to call from any component — does NOT create a Firestore listener.
 */
export const useGuides = (): UseGuidesReturn => {
  const store = useGuidesStore();

  const hasGuide = useCallback(
    (pageId: string) => store.hasPublishedGuide(pageId),
    [store]
  );

  const getGuide = useCallback(
    (pageId: string) => store.getGuide(pageId),
    [store]
  );

  const publishedGuides = store.getPublishedGuides();

  return {
    hasGuide,
    getGuide,
    publishedGuides,
    allGuides: store.guides,
    isLoading: store.isLoading,
  };
};
